import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectContent, SelectItem, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useData } from "@/context/DataContext";
import { todayISO } from "@/lib/date";
import { toast } from "sonner";
import { Sparkle } from "@phosphor-icons/react";
import { api } from "@/lib/api";

export default function QuickAddModal({ open, onOpenChange, defaultDate }) {
  const { categories, createEvent, createTask, createImportant } = useData();
  const [type, setType] = useState("event");
  const [nlpText, setNlpText] = useState("");
  const [parsing, setParsing] = useState(false);

  // shared
  const [title, setTitle] = useState("");
  const [date, setDate] = useState(defaultDate || todayISO());
  const [categoryId, setCategoryId] = useState("");
  const [notes, setNotes] = useState("");

  // event
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("10:00");
  const [location, setLocation] = useState("");
  const [kind, setKind] = useState("flexible");
  const [recurrence, setRecurrence] = useState("none");

  // task
  const [priority, setPriority] = useState("should");
  const [dueTime, setDueTime] = useState("");
  const [scope, setScope] = useState("day");

  // important
  const [impKind, setImpKind] = useState("other");

  const reset = () => {
    setTitle(""); setNotes(""); setLocation("");
    setStartTime("09:00"); setEndTime("10:00");
    setDueTime(""); setCategoryId("");
    setPriority("should"); setKind("flexible"); setImpKind("other");
    setRecurrence("none");
    setNlpText("");
  };

  const parseNlp = async () => {
    if (!nlpText.trim()) return;
    setParsing(true);
    try {
      const { data } = await api.post("/nlp/parse", { text: nlpText, today: todayISO() });
      // Apply the parsed fields
      if (data.type) setType(data.type);
      if (data.title) setTitle(data.title);
      if (data.date) setDate(data.date);
      if (data.category_id) setCategoryId(data.category_id);
      if (data.notes) setNotes(data.notes);
      if (data.location) setLocation(data.location);
      if (data.type === "event") {
        if (data.start_time) setStartTime(data.start_time);
        if (data.end_time) setEndTime(data.end_time);
        if (data.kind) setKind(data.kind);
        if (data.recurrence) setRecurrence(data.recurrence);
      } else if (data.type === "task") {
        if (data.priority) setPriority(data.priority);
        if (data.start_time) setDueTime(data.start_time);
      } else if (data.type === "important") {
        if (data.important_kind) setImpKind(data.important_kind);
      }
      toast.success(`Herkend als ${data.type === "event" ? "afspraak" : data.type === "task" ? "taak" : "belangrijke datum"}`);
    } catch (e) {
      toast.error("Kon dat niet begrijpen. Probeer specifieker te zijn.");
    } finally { setParsing(false); }
  };

  const submit = async () => {
    if (!title.trim()) return toast.error("Title required");
    try {
      if (type === "event") {
        await createEvent({
          title, date, start_time: startTime, end_time: endTime,
          kind, category_id: categoryId || null, location: location || null,
          notes: notes || null, recurrence,
        });
        toast.success("Afspraak toegevoegd");
      } else if (type === "task") {
        await createTask({
          title, date, priority, scope,
          due_time: dueTime || null,
          category_id: categoryId || null,
          notes: notes || null, subtasks: [],
        });
        toast.success("Taak toegevoegd");
      } else {
        await createImportant({
          title, date, kind: impKind,
          category_id: categoryId || null, notes: notes || null,
        });
        toast.success("Belangrijke datum toegevoegd");
      }
      reset();
      onOpenChange(false);
    } catch (e) {
      toast.error("Toevoegen mislukt");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#121C2F] border-slate-800 text-slate-100 max-w-lg"
        data-testid="quick-add-modal"
      >
        <DialogHeader>
          <DialogTitle className="text-slate-50 text-xl">Snel toevoegen</DialogTitle>
        </DialogHeader>

        {/* Natural-language input */}
        <div className="mt-3 rounded-lg border border-blue-500/20 bg-blue-600/5 p-3">
          <div className="flex items-center gap-2 mb-2">
            <Sparkle size={14} weight="fill" className="text-blue-400" />
            <div className="text-[11px] font-semibold uppercase tracking-widest text-blue-300">Vraag het Nova</div>
          </div>
          <div className="flex gap-2">
            <Input
              data-testid="qa-nlp-input"
              value={nlpText}
              onChange={(e) => setNlpText(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !parsing && parseNlp()}
              placeholder='Bijv. "biologie leren morgen 15u 2u" of "wiskunde tentamen volgende vrijdag"'
              className="bg-slate-900 border-slate-800 text-slate-100 h-9"
            />
            <Button
              data-testid="qa-nlp-parse"
              onClick={parseNlp}
              disabled={parsing || !nlpText.trim()}
              className="bg-blue-600 hover:bg-blue-500 h-9 shrink-0"
            >
              {parsing ? "..." : "Herken"}
            </Button>
          </div>
        </div>

        <Tabs value={type} onValueChange={setType} className="mt-2">
          <TabsList className="grid grid-cols-3 bg-slate-900 border border-slate-800">
            <TabsTrigger value="event" data-testid="qa-tab-event">Afspraak</TabsTrigger>
            <TabsTrigger value="task" data-testid="qa-tab-task">Taak</TabsTrigger>
            <TabsTrigger value="important" data-testid="qa-tab-important">Belangrijk</TabsTrigger>
          </TabsList>

          <div className="mt-4 space-y-4">
            <div>
              <Label className="text-slate-300 text-xs uppercase tracking-wider">Titel</Label>
              <Input
                data-testid="qa-title-input"
                autoFocus
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="mt-1.5 bg-slate-900 border-slate-800 text-slate-100"
                placeholder="Wat plan je?"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-300 text-xs uppercase tracking-wider">Datum</Label>
                <Input
                  data-testid="qa-date-input"
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="mt-1.5 bg-slate-900 border-slate-800 text-slate-100"
                />
              </div>
              <div>
                <Label className="text-slate-300 text-xs uppercase tracking-wider">Categorie</Label>
                <Select value={categoryId} onValueChange={setCategoryId}>
                  <SelectTrigger data-testid="qa-category-select" className="mt-1.5 bg-slate-900 border-slate-800 text-slate-100">
                    <SelectValue placeholder="Kies een categorie" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800 text-slate-100">
                    {categories.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        <span className="inline-flex items-center gap-2">
                          <span className="w-2.5 h-2.5 rounded-full" style={{ background: c.color }} />
                          {c.name}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <TabsContent value="event" className="space-y-4 mt-0">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-slate-300 text-xs uppercase tracking-wider">Start</Label>
                  <Input data-testid="qa-start-input" type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} className="mt-1.5 bg-slate-900 border-slate-800 text-slate-100" />
                </div>
                <div>
                  <Label className="text-slate-300 text-xs uppercase tracking-wider">Einde</Label>
                  <Input data-testid="qa-end-input" type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} className="mt-1.5 bg-slate-900 border-slate-800 text-slate-100" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-slate-300 text-xs uppercase tracking-wider">Type</Label>
                  <Select value={kind} onValueChange={setKind}>
                    <SelectTrigger className="mt-1.5 bg-slate-900 border-slate-800 text-slate-100">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-800 text-slate-100">
                      <SelectItem value="fixed">Vast</SelectItem>
                      <SelectItem value="flexible">Flexibel</SelectItem>
                      <SelectItem value="rest">Rust</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-300 text-xs uppercase tracking-wider">Herhaalt</Label>
                  <Select value={recurrence} onValueChange={setRecurrence}>
                    <SelectTrigger className="mt-1.5 bg-slate-900 border-slate-800 text-slate-100" data-testid="qa-recurrence-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-800 text-slate-100">
                      <SelectItem value="none">Herhaalt niet</SelectItem>
                      <SelectItem value="daily">Elke dag</SelectItem>
                      <SelectItem value="weekly">Elke week</SelectItem>
                      <SelectItem value="weekdays">Werkdagen (ma–vr)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-slate-300 text-xs uppercase tracking-wider">Locatie</Label>
                <Input value={location} onChange={(e) => setLocation(e.target.value)} className="mt-1.5 bg-slate-900 border-slate-800 text-slate-100" placeholder="Optioneel" />
              </div>
            </TabsContent>

            <TabsContent value="task" className="space-y-4 mt-0">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-slate-300 text-xs uppercase tracking-wider">Prioriteit</Label>
                  <Select value={priority} onValueChange={setPriority}>
                    <SelectTrigger data-testid="qa-priority-select" className="mt-1.5 bg-slate-900 border-slate-800 text-slate-100">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-800 text-slate-100">
                      <SelectItem value="must">Moet doen</SelectItem>
                      <SelectItem value="should">Zou moeten</SelectItem>
                      <SelectItem value="if_time">Als er tijd is</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-300 text-xs uppercase tracking-wider">Streeftijd</Label>
                  <Input type="time" value={dueTime} onChange={(e) => setDueTime(e.target.value)} className="mt-1.5 bg-slate-900 border-slate-800 text-slate-100" />
                </div>
              </div>
              <div>
                <Label className="text-slate-300 text-xs uppercase tracking-wider">Bereik</Label>
                <Select value={scope} onValueChange={setScope}>
                  <SelectTrigger className="mt-1.5 bg-slate-900 border-slate-800 text-slate-100">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800 text-slate-100">
                    <SelectItem value="day">Dagtaak</SelectItem>
                    <SelectItem value="week">Weektaak</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </TabsContent>

            <TabsContent value="important" className="space-y-4 mt-0">
              <div>
                <Label className="text-slate-300 text-xs uppercase tracking-wider">Soort</Label>
                <Select value={impKind} onValueChange={setImpKind}>
                  <SelectTrigger className="mt-1.5 bg-slate-900 border-slate-800 text-slate-100">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800 text-slate-100">
                    <SelectItem value="exam">Toets/Tentamen</SelectItem>
                    <SelectItem value="deadline">Deadline</SelectItem>
                    <SelectItem value="shift">Werkdienst</SelectItem>
                    <SelectItem value="appointment">Afspraak</SelectItem>
                    <SelectItem value="birthday">Verjaardag</SelectItem>
                    <SelectItem value="trip">Reis</SelectItem>
                    <SelectItem value="concert">Concert</SelectItem>
                    <SelectItem value="other">Overig</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </TabsContent>

            <div>
              <Label className="text-slate-300 text-xs uppercase tracking-wider">Notities</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="mt-1.5 bg-slate-900 border-slate-800 text-slate-100 min-h-[70px]"
                placeholder="Optionele notities"
              />
            </div>
          </div>
        </Tabs>
        <div className="flex justify-end gap-2 mt-2">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="text-slate-400 hover:text-slate-100 hover:bg-slate-800/60"
          >
            Annuleren
          </Button>
          <Button
            data-testid="qa-submit-button"
            onClick={submit}
            className="bg-blue-600 hover:bg-blue-500 text-white"
          >
            Toevoegen
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
